/**
 * Phase 8 T-06b §20.C · Collapsible reasoning trace above each assistant turn.
 *
 * Collapsed row: `▸ Thought for {N}s · {M} orchestrator steps`.
 * Expanded: vertical list of steps with name + duration.
 *
 * Keyboard: Space / Enter toggles. `aria-expanded` reflects state.
 */

import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import type { AgentReasoningStep } from '../hooks/useAgentThread';

interface Props {
  thoughtMs: number;
  steps: AgentReasoningStep[];
}

export function AgentReasoningTrace({ thoughtMs, steps }: Props) {
  const [open, setOpen] = useState(false);
  const thoughtSeconds = (thoughtMs / 1000).toFixed(1);

  return (
    <div className="mb-1">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 rounded-[6px] px-1.5 py-0.5 text-[11px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
        style={{ color: 'var(--vscode-text-tertiary)' }}
      >
        <ChevronRight
          className={`h-3 w-3 transition-transform ${open ? 'rotate-90' : ''}`}
          aria-hidden
        />
        <span>
          Thought for {thoughtSeconds}s · {steps.length} orchestrator step{steps.length === 1 ? '' : 's'}
        </span>
      </button>
      {open ? (
        <ul
          className="mt-1.5 ml-5 flex flex-col gap-1 border-l pl-3 text-[11px]"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
        >
          {steps.map((s, i) => (
            <li
              key={`${s.name}-${i}`}
              className="flex items-baseline justify-between gap-3"
              style={{ color: 'var(--vscode-text-secondary)' }}
            >
              <span className="font-mono">{s.name}</span>
              <span style={{ color: 'var(--vscode-text-tertiary)' }}>
                {(s.durationMs / 1000).toFixed(2)}s
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
